import { useCallback, useRef, useState } from 'react';
import { authMode, getDevUser, logout, setDevUser } from '../api/auth';
import { DEV_USERS } from '../api/devUsers';
import { useEscape } from '../hooks/useEscape';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { initials } from '../lib/format';
import { Icon, IconSpacer } from './Icon';

interface UserMenuProps {
  name: string;
  subtitle: string;
  /** Opens "My profile" (active members only). */
  onProfile?: () => void;
}

/**
 * Avatar + account menu: who you are, My profile and Sign out (in dev auth mode, a "View as" picker for the seeded users
 * instead). Moving between areas and uploading are the visible header buttons, so they aren't repeated here.
 */
export function UserMenu({ name, subtitle, onProfile }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const refs = useRef([wrap]).current;
  const close = useCallback(() => setOpen(false), []);
  useOutsideClick(refs, close, open);
  useEscape(close, open);
  const current = getDevUser();

  const viewAs = (id: string) => {
    setOpen(false);
    if (id === current) return;
    setDevUser(id);
    // Full reload: every cached query and mounted component must start over as the new user,
    // otherwise parts of the page keep the previous user's permissions until the next refresh.
    window.location.assign('/');
  };

  return (
    <div style={{ position: 'relative' }} ref={wrap}>
      <button
        type="button"
        className="avatar"
        id="avatarBtn"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {initials(name)}
      </button>
      <div className={`menu${open ? ' open' : ''}`} id="userMenu" role="menu" onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '10px 10px 6px' }}>
          <b>{name}</b>
          <div style={{ color: 'var(--muted)', fontSize: 12 }}>{subtitle}</div>
        </div>
        <hr />
        {onProfile && (
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onProfile();
            }}
          >
            <Icon name="users" />
            My profile
          </button>
        )}
        {onProfile && <hr />}
        {authMode === 'dev' ? (
          <>
            <div className="lbl">Dev: view as</div>
            {DEV_USERS.map((u) => (
              <button
                type="button"
                key={u.id}
                role="menuitemradio"
                aria-checked={u.id === current}
                className={u.id === current ? 'on' : ''}
                onClick={() => viewAs(u.id)}
                title={u.hint}
              >
                {u.id === current ? <Icon name="check" /> : <IconSpacer />}
                {u.name}
              </button>
            ))}
          </>
        ) : (
          <button type="button" role="menuitem" onClick={() => logout()}>
            <Icon name="logout" />
            Sign out
          </button>
        )}
      </div>
    </div>
  );
}
